import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ShieldOff, Unlock, Loader2, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm} ${hh}:${mi}`;
  } catch { return iso; }
};

const LockedAccountsCard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlockingId, setUnlockingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/locked-accounts`, { headers: authHeaders() });
      setRows(r.data?.rows || []);
    } catch { /* silent — likely 403 for non-admin */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const handleUnlock = async (row) => {
    const ok = window.confirm(
      `Unlock ${row.display_name} (${row.principal})?\n\nIP: ${row.ip}\nFailed attempts: ${row.failed_count}\nAuto-unlock in: ${row.minutes_left} min\n\nProceeding will clear the lockout immediately so they can try again.`
    );
    if (!ok) return;
    setUnlockingId(row.identifier);
    try {
      await axios.post(
        `${API}/api/admin/locked-accounts/unlock`,
        { source: row.source, identifier: row.identifier },
        { headers: authHeaders() }
      );
      toast({ title: 'Account unlocked', description: `${row.display_name} can now log in.` });
      setRows(prev => prev.filter(r => r.identifier !== row.identifier));
    } catch (e) {
      toast({
        title: 'Unlock failed',
        description: e?.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUnlockingId(null);
    }
  };

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 shadow-md" data-testid="locked-accounts-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className="bg-amber-100 p-2 rounded-lg">
              <ShieldOff className="h-5 w-5 text-amber-700" />
            </div>
            <div>
              <h3 className="font-bold text-amber-900">Locked Accounts</h3>
              <p className="text-xs text-amber-700">
                {rows.length} account{rows.length > 1 ? 's are' : ' is'} temporarily locked after too many failed attempts
              </p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-2">
          {rows.map(r => (
            <div
              key={r.identifier}
              className="bg-white rounded-lg p-2.5 border border-amber-200 flex items-center gap-2"
              data-testid={`locked-row-${r.principal}`}
            >
              {r.photo_url ? (
                <img
                  src={r.photo_url.startsWith('http') ? r.photo_url : `${API}${r.photo_url}`}
                  alt={r.display_name}
                  className="h-9 w-9 rounded-full object-cover border-2 border-amber-200 flex-shrink-0"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="h-4 w-4 text-amber-700" />
                </div>
              )}

              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-gray-900 truncate">
                  {r.display_name}
                  <span className="text-[10px] font-mono text-gray-400 ml-1">· {r.principal}</span>
                  <Badge
                    variant="outline"
                    className={`ml-1.5 text-[9px] py-0 ${r.source === 'admin'
                      ? 'border-rose-300 text-rose-700 bg-rose-50'
                      : 'border-sky-300 text-sky-700 bg-sky-50'}`}
                  >
                    {r.source}
                  </Badge>
                </p>
                <p className="text-[10px] text-gray-500 truncate">
                  {r.failed_count} fails · IP {r.ip} · last {fmtTime(r.last_attempt_at)}
                </p>
              </div>

              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold text-amber-700 leading-none">{r.minutes_left}m</p>
                <p className="text-[9px] text-gray-500">left</p>
              </div>

              <Button
                size="sm"
                variant="outline"
                onClick={() => handleUnlock(r)}
                disabled={unlockingId === r.identifier}
                className="border-amber-300 text-amber-800 hover:bg-amber-100 h-8 px-2 flex-shrink-0"
                data-testid={`unlock-${r.principal}`}
                title="Unlock this account now"
              >
                {unlockingId === r.identifier ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <>
                    <Unlock className="h-3.5 w-3.5 mr-1" /> Unlock
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default LockedAccountsCard;
